import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase, type Tables, unwrap } from '@/supabase';

export type Thread = Tables<'chat_threads'>;
export type Message = Tables<'chat_messages'>;
export type Action = Tables<'ai_actions'>;

export interface Attachment {
  path: string;
  mime: string;
  bytes?: number;
  duration_ms?: number;
  transcript?: string;
}

export const attachmentsOf = (m: Message) =>
  ((m.payload as { attachments?: Attachment[] } | null)?.attachments ?? []) as Attachment[];

export const threadsKey = ['threads'] as const;
export const messagesKey = (id: string) => ['messages', id] as const;
export const actionsKey = (id: string) => ['actions', id] as const;

export const useThreads = () =>
  useQuery({
    queryKey: threadsKey,
    queryFn: async () =>
      unwrap(
        await supabase
          .from('chat_threads')
          .select()
          .eq('archived', false)
          .order('last_message_at', { ascending: false }),
      ),
  });

export const useMessages = (threadId: string) =>
  useQuery({
    queryKey: messagesKey(threadId),
    queryFn: async () =>
      unwrap(
        await supabase
          .from('chat_messages')
          .select()
          .eq('thread_id', threadId)
          .order('created_at', { ascending: false })
          .limit(200),
      ).reverse(),
  });

export const useActions = (threadId: string) =>
  useQuery({
    queryKey: actionsKey(threadId),
    queryFn: async () =>
      new Map(
        unwrap(await supabase.from('ai_actions').select().eq('thread_id', threadId)).map((a) => [
          a.id,
          a,
        ]),
      ),
  });

/** Adds or replaces a message in the open conversation's list, keeping time order. */
export function putMessage(qc: QueryClient, m: Message) {
  qc.setQueryData<Message[]>(messagesKey(m.thread_id), (list) => {
    if (!list) return list;
    const rest = list.filter((x) => x.id !== m.id);
    return [...rest, m].sort((a, b) => a.created_at.localeCompare(b.created_at));
  });
}

export function putAction(qc: QueryClient, a: Action) {
  qc.setQueryData<Map<string, Action>>(actionsKey(a.thread_id), (map) =>
    map ? new Map(map).set(a.id, a) : map,
  );
}

/**
 * Keeps an open conversation live: FarmAI's messages, a voice note's transcript and card
 * status arrive by Realtime too, e.g. from the user's other phone.
 */
export function useLiveThread(threadId: string) {
  const qc = useQueryClient();
  useEffect(() => {
    const channel = supabase
      .channel(`thread:${threadId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_messages',
          filter: `thread_id=eq.${threadId}`,
        },
        (p) => p.new && 'id' in p.new && putMessage(qc, p.new as Message),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'ai_actions', filter: `thread_id=eq.${threadId}` },
        (p) => p.new && 'id' in p.new && putAction(qc, p.new as Action),
      )
      .subscribe();
    return () => void supabase.removeChannel(channel);
  }, [qc, threadId]);
}

/** New messages anywhere (e.g. an update in the inbox) refresh the list and its unread dots. */
export function useLiveThreads(uid: string | undefined) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!uid) return;
    const channel = supabase
      .channel('threads')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'chat_threads', filter: `owner_id=eq.${uid}` },
        () => void qc.invalidateQueries({ queryKey: threadsKey }),
      )
      .subscribe();
    return () => void supabase.removeChannel(channel);
  }, [qc, uid]);
}

export const unread = (t: Thread) => t.last_message_at > t.last_read_at;

export async function markRead(threadId: string) {
  await supabase
    .from('chat_threads')
    .update({ last_read_at: new Date().toISOString() })
    .eq('id', threadId);
}

/** A short-lived link to a private file (voice note, photo). */
export const useMediaUrl = (path: string) =>
  useQuery({
    queryKey: ['media', path],
    staleTime: 8 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.storage.from('chat-media').createSignedUrl(path, 600);
      if (error) throw error;
      return data.signedUrl;
    },
  });
